import { Flex } from 'antd';

import Avatar from '@/UI/Avatar/Avatar';
import { Caption, Meta } from '@/UI/typography';
import {
  formatDateTime,
  formatShortDate,
  formatShortDateTime,
} from '@/utils/formatDate';

import type { AvatarColor } from '@/UI/Avatar/Avatar';
import type { ReactNode } from 'react';

type EntityCellProps = {
  name: string;
  title: ReactNode;
  description?: ReactNode;
  image?: string | null;
  color?: AvatarColor;
  imageFit?: 'cover' | 'contain';
  tooltipLabel?: string;
  to?: string;
  extra?: ReactNode;
};

export const EntityCell = ({
  name,
  title,
  description,
  image,
  color,
  imageFit,
  tooltipLabel,
  to,
  extra,
}: EntityCellProps) => (
  <Avatar
    size="small"
    name={name}
    src={image}
    color={color}
    imageFit={imageFit}
    tooltipLabel={tooltipLabel}
    title={title}
    titleExtra={extra}
    description={description}
    to={to}
  />
);

export const MissingValueCell = () => <Meta>—</Meta>;

export const MoneyCell = ({
  amount,
  currency,
  locale,
}: {
  amount: number | null;
  currency: string;
  locale: string;
}) =>
  amount === null ? (
    <MissingValueCell />
  ) : (
    <Meta>
      {new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount)}
    </Meta>
  );

export const NumberCell = ({
  value,
  locale,
}: {
  value: number | null;
  locale?: string;
}) =>
  value === null ? (
    <MissingValueCell />
  ) : (
    <Meta>{new Intl.NumberFormat(locale).format(value)}</Meta>
  );

export const DateCell = ({
  value,
  locale,
}: {
  value: string | null;
  locale: string;
}) =>
  value ? <Meta>{formatShortDate(value, locale)}</Meta> : <MissingValueCell />;

export const DateTimeCell = ({
  value,
  locale,
  compact = false,
}: {
  value: string | null;
  locale: string;
  compact?: boolean;
}) =>
  value ? (
    <Meta>
      {(compact ? formatShortDateTime : formatDateTime)(value, locale)}
    </Meta>
  ) : (
    <MissingValueCell />
  );

export const NoteCell = ({ children }: { children: ReactNode }) => (
  <Caption type="secondary" italic>
    {children}
  </Caption>
);

export const MetadataCell = ({ children }: { children: ReactNode }) => (
  <Meta ellipsis={{ tooltip: true }}>{children}</Meta>
);

export const TwoLineCell = ({ children }: { children: ReactNode }) => (
  <Meta ellipsis={{ rows: 2, tooltip: true }}>{children}</Meta>
);

export const ActionCell = ({ children }: { children: ReactNode }) => (
  <Flex
    justify="flex-end"
    onClick={(event) => event.stopPropagation()}
    onPointerDown={(event) => event.stopPropagation()}
  >
    {children}
  </Flex>
);
